import { FC, HTMLAttributes } from "react";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";

import { env } from "@/env.mjs";
import { IStrapiImage } from "@/api/dtos/shared-component.type";

// ----------------------------------------------------------------------------
// Full-width dark strip with a centered "Trusted by our Clients" label
// above a horizontally-scrolling marquee of client logos. Sits directly
// below StatsStripSection — both share bg-black-950 and zero outer
// margin so they read as one continuous proof band.
//
// Marquee: render the client list twice in a flex row and translate the
// row by -50% on an infinite linear loop. Two copies are always visible
// so the wrap point is invisible. Pauses on hover.
// ----------------------------------------------------------------------------

export type ITrustedByClient = {
  id?: number;
  href?: string;
  // Direct Strapi media (not wrapped in shared.image component), so the
  // shape is { data: { id, attributes: { url, name, ... } } } — see the
  // cards/trusted-by-client.json schema.
  image?: IStrapiImage;
};

export type ITrustedByStripSectionProps = HTMLAttributes<HTMLElement> & {
  label?: string;
  clients?: ITrustedByClient[];
};

const renderClient = (client: ITrustedByClient, key: string | number) => {
  const mediaUrl = client.image?.data?.attributes?.url;
  if (!mediaUrl) return null;
  const logoSrc = `${env.NEXT_PUBLIC_MEDIA_URL}${mediaUrl}`;

  const alt =
    client.image?.data?.attributes?.alternativeText ||
    client.image?.data?.attributes?.name ||
    "Client logo";

  // Fixed-size cell with the logo contained inside, so a tiny square logo
  // (e.g. one97) and a wide wordmark (e.g. Hive Medical) take up the same
  // amount of horizontal space. Without this, narrow logos look puny next
  // to wide ones in the same row.
  const cell = (
    <span className="flex h-8 w-28 items-center justify-center md:h-10 md:w-36 lg:h-12 lg:w-40">
      <Image
        src={logoSrc}
        alt={alt}
        width={160}
        height={48}
        className="max-h-full max-w-full object-contain opacity-80 brightness-0 invert transition-opacity hover:opacity-100"
      />
    </span>
  );

  if (client.href) {
    return (
      <Link
        key={key}
        href={client.href}
        target="_blank"
        // nofollow: trusted-by logos are social proof, not endorsements we
        // want to pass PageRank to. Standard practice for showcase grids.
        rel="noopener noreferrer nofollow"
        className="flex shrink-0 items-center"
      >
        {cell}
      </Link>
    );
  }
  return (
    <span key={key} className="flex shrink-0 items-center">
      {cell}
    </span>
  );
};

const TrustedByStripSection: FC<ITrustedByStripSectionProps> = (props) => {
  const {
    label = "Trusted by our Clients",
    clients,
    className,
    ...other
  } = props;

  if (!clients || clients.length === 0) return null;

  // For the seamless-loop marquee to *look* like a marquee instead of two
  // visible duplicates, each "copy" of the client list must be at least one
  // viewport wide. With few logos (e.g. 2), the natural copy is narrow and
  // both copies show up on screen at once. We pad each copy by repeating
  // the source list until it has enough items to dominate the viewport.
  const MIN_LOGOS_PER_COPY = 8;
  const repeatTimes = Math.max(
    1,
    Math.ceil(MIN_LOGOS_PER_COPY / clients.length),
  );
  const paddedClients = Array.from({ length: repeatTimes }).flatMap(() =>
    clients,
  );

  return (
    <section
      className={clsx("bg-black-950 py-8 md:py-10 lg:py-12", className)}
      {...other}
    >
      {/* Centered label */}
      <div className="mb-6 text-center md:mb-8">
        <span className="font-bricolage-grotesque text-sm font-medium text-white/70 md:text-base">
          {label}
        </span>
      </div>

      {/* Full-width marquee viewport. Soft edge masks fade logos in/out at
          the screen boundaries so the loop seam is invisible. */}
      <div
        className="group w-full overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)",
        }}
      >
        {/* Marquee row: two identical copies of the client list, side by side.
            The outer row animates by -50% which is exactly one copy's width,
            making the wrap point invisible. Both copies are wrapped in
            identical flex divs so the -50% translation lands cleanly. */}
        <div className="flex w-max animate-trusted-marquee group-hover:[animation-play-state:paused]">
          <ul className="flex shrink-0 items-center gap-x-12 pr-12 md:gap-x-16 md:pr-16 lg:gap-x-20 lg:pr-20">
            {paddedClients.map((c, i) => (
              <li
                key={`a-${i}`}
                className="flex shrink-0 list-none items-center"
              >
                {renderClient(c, `a-${i}`)}
              </li>
            ))}
          </ul>
          {/* Duplicate copy — aria-hidden so screen readers don't read each
              client twice. Identical structure so -50% translation aligns
              perfectly with the original list's width. */}
          <ul
            className="flex shrink-0 items-center gap-x-12 pr-12 md:gap-x-16 md:pr-16 lg:gap-x-20 lg:pr-20"
            aria-hidden="true"
          >
            {paddedClients.map((c, i) => (
              <li
                key={`b-${i}`}
                className="flex shrink-0 list-none items-center"
              >
                {renderClient(c, `b-${i}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default TrustedByStripSection;
